import dayjs from 'dayjs';

import type {
  ManualIssueGrant,
  ManualIssueGrantListPageInfo,
  ManualIssueGrantListResponse,
  ManualIssueStats,
  ManualIssueStatsBlock,
} from './types';

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function unwrap(raw: unknown): unknown {
  if (!isRecord(raw)) return raw;
  if (isRecord(raw.data)) return raw.data;
  return raw;
}

export function getDefaultStatsBlock(): ManualIssueStatsBlock {
  return {
    grantCount: 0,
    voucherCount: 0,
    distinctUsers: 0,
  };
}

export function getDefaultStats(): ManualIssueStats {
  return {
    today: getDefaultStatsBlock(),
    allTime: getDefaultStatsBlock(),
    lastGrant: null,
  };
}

function normalizeStatsBlock(raw: unknown): ManualIssueStatsBlock {
  const fallback = getDefaultStatsBlock();
  if (!isRecord(raw)) return fallback;

  return {
    grantCount: Number(raw.grantCount ?? raw.grant_count ?? 0) || 0,
    voucherCount: Number(raw.voucherCount ?? raw.voucher_count ?? 0) || 0,
    distinctUsers: Number(raw.distinctUsers ?? raw.distinct_users ?? 0) || 0,
  };
}

function normalizeGrant(raw: unknown): ManualIssueGrant | null {
  if (!isRecord(raw)) return null;

  return {
    id: String(raw.id ?? ''),
    requestId: String(raw.requestId ?? raw.request_id ?? ''),
    userId: String(raw.userId ?? raw.user_id ?? ''),
    walletAddress: String(raw.walletAddress ?? raw.wallet_address ?? ''),
    count: Number(raw.count ?? 0) || 0,
    reasonCategory: String(raw.reasonCategory ?? raw.reason_category ?? ''),
    note: String(raw.note ?? ''),
    operator: String(raw.operator ?? ''),
    createdAt: String(raw.createdAt ?? raw.created_at ?? ''),
  };
}

export function normalizeStats(raw: unknown): ManualIssueStats {
  const source = unwrap(raw);
  const fallback = getDefaultStats();

  if (!isRecord(source)) return fallback;

  return {
    today: normalizeStatsBlock(source.today),
    allTime: normalizeStatsBlock(source.allTime ?? source.all_time),
    lastGrant: source.lastGrant ? normalizeGrant(source.lastGrant) : normalizeGrant(source.last_grant),
  };
}

function normalizePageInfo(raw: unknown, fallbackLimit: number): ManualIssueGrantListPageInfo {
  if (!isRecord(raw)) return { page: 1, limit: fallbackLimit, totalCount: 0 };

  return {
    page: Number(raw.page ?? 1) || 1,
    limit: Number(raw.limit ?? fallbackLimit) || fallbackLimit,
    totalCount: Number(raw.totalCount ?? raw.total_count ?? raw.total ?? 0) || 0,
  };
}

export function normalizeGrantList(raw: unknown, fallbackLimit = 50): ManualIssueGrantListResponse {
  const source = unwrap(raw);

  if (!isRecord(source)) {
    return { items: [], pageInfo: { page: 1, limit: fallbackLimit, totalCount: 0 } };
  }

  const itemsRaw = Array.isArray(source.items)
    ? source.items
    : Array.isArray(source.list)
      ? source.list
      : [];

  const items = itemsRaw.map(normalizeGrant).filter((item): item is ManualIssueGrant => Boolean(item));
  const pageInfo = normalizePageInfo(source.pageInfo ?? source.page_info, fallbackLimit);

  return { items, pageInfo };
}

export function generateRequestId(): string {
  const cryptoObj = (globalThis as any).crypto;
  if (cryptoObj && typeof cryptoObj.randomUUID === 'function') {
    return cryptoObj.randomUUID();
  }

  return `xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx`.replace(/[xy]/g, (c) => {
    const r = Math.floor(Math.random() * 16);
    const v = c === 'x' ? r : (r % 4) + 8;
    return v.toString(16);
  });
}

export function formatDateTime(value?: string | null) {
  if (!value) return '--';
  const date = dayjs(value);
  return date.isValid() ? date.format('YYYY-MM-DD HH:mm:ss') : String(value);
}

export function formatInt(value?: number | string | null) {
  const num = Number(value ?? 0);
  return new Intl.NumberFormat('en-US').format(Number.isFinite(num) ? num : 0);
}

export function shortenWallet(address?: string | null) {
  if (!address) return '--';
  if (address.length <= 10) return address;
  return `${address.slice(0, 5)}...${address.slice(-5)}`;
}

export function getErrorMessage(error: unknown) {
  if (!error) return '';
  if (typeof error === 'string') return error;
  if (error instanceof Error) return error.message;

  if (isRecord(error)) {
    if (typeof error.message === 'string') return error.message;
    if (typeof error.error === 'string') return error.error;
    if (isRecord(error.response) && isRecord(error.response.data)) {
      const data = error.response.data;
      if (typeof data.message === 'string') return data.message;
      if (typeof data.error === 'string') return data.error;
    }
  }

  return String(error);
}
